<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="420px"
    :close-on-click-modal="false"
    custom-class="legal-auth-qr-dialog legal-auth-qr-dialog--legal"
  >
    <div class="qr-dialog-body">
      <p class="qr-tip">{{ tipText }}</p>
      <div class="qr-code-wrapper">
        <div class="qr-code-box" v-loading="qrGenerating">
          <img v-if="qrCodeUrl" :src="qrCodeUrl" class="qr-code-image" alt="法人认证二维码" />
          <span v-else class="qr-code-empty">二维码生成中</span>
        </div>
      </div>
      <p class="qr-company">
        <span class="qr-company-label">认证企业：</span><span class="qr-company-name">{{ getCertificationCompanyName() }}</span>
      </p>
      <p v-if="qrScanned" class="qr-success" role="status" aria-live="polite">
        <i class="el-icon-circle-check"></i>
        <span>扫码成功，请在手机端继续完成认证</span>
      </p>
      <p v-else class="qr-expire">
        二维码有效期 <span class="qr-countdown">{{ qrCountdown }}</span>秒，请勿转发
      </p>
    </div>
  </el-dialog>
</template>

<script>
import QRCode from 'qrcode'
import { connectMarketSocket, subscribeMarketSocketMessage } from '@/utils/marketSocket'

const AUTH_URL = process.env.VUE_APP_AUTH_URL

export default {
  name: 'LegalAuthQrDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    dialogTitle: {
      type: String,
      default: '法人实名认证'
    },
    companyName: {
      type: String,
      default: ''
    },
    tipText: {
      type: String,
      default: '请法定代表人本人使用微信扫描二维码'
    }
  },
  data() {
    return {
      qrCountdown: 119,
      qrTimer: null,
      qrCodeUrl: '',
      qrGenerating: false,
      qrGenerationId: 0,
      qrScanned: false,
      faceCheckHandled: false,
      unsubscribeMarketSocket: null
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.prepareQrCode()
      } else {
        this.resetQrState()
      }
    }
  },
  created() {
    this.unsubscribeMarketSocket = subscribeMarketSocketMessage(this.handleMarketSocketMessage)
  },
  beforeDestroy() {
    this.resetQrState()
    if (this.unsubscribeMarketSocket) this.unsubscribeMarketSocket()
  },
  methods: {
    getCertificationCompanyName() {
      return this.companyName || localStorage.getItem('companyName') || '--'
    },
    async prepareQrCode() {
      this.resetQrState()
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('登录信息已失效，请重新登录')
        this.dialogVisible = false
        return
      }

      connectMarketSocket()
      const generationId = this.qrGenerationId
      this.qrGenerating = true
      try {
        const authUrl = new URL(AUTH_URL)
        authUrl.searchParams.set('tokens', token)
        const qrCodeUrl = await QRCode.toDataURL(authUrl.toString(), {
          width: 200,
          margin: 1,
          errorCorrectionLevel: 'M'
        })
        if (generationId !== this.qrGenerationId || !this.visible) return
        this.qrCodeUrl = qrCodeUrl
        if (!this.qrScanned) this.startQrTimer()
      } catch (error) {
        if (generationId !== this.qrGenerationId) return
        this.$message.error('二维码生成失败，请稍后重试')
        this.dialogVisible = false
      } finally {
        if (generationId === this.qrGenerationId) this.qrGenerating = false
      }
    },
    handleMarketSocketMessage(message) {
      if (!this.visible || !message || Number(message.code) !== 200) return
      if (message.type === 'verify_scanned') {
        this.qrScanned = true
        this.stopQrTimer()
        return
      }
      if (message.type !== 'verify_face_check' || this.faceCheckHandled) return

      this.faceCheckHandled = true
      this.stopQrTimer()
      this.$emit('face-check-success', message)
      this.dialogVisible = false
    },
    startQrTimer() {
      this.stopQrTimer()
      this.qrTimer = window.setInterval(() => {
        if (this.qrCountdown <= 0) {
          this.stopQrTimer()
          this.$message.warning('二维码已过期，请重新提交')
          this.dialogVisible = false
          return
        }
        this.qrCountdown--
      }, 1000)
    },
    stopQrTimer() {
      if (!this.qrTimer) return
      window.clearInterval(this.qrTimer)
      this.qrTimer = null
    },
    resetQrState() {
      this.qrGenerationId++
      this.qrCountdown = 119
      this.qrCodeUrl = ''
      this.qrGenerating = false
      this.qrScanned = false
      this.faceCheckHandled = false
      this.stopQrTimer()
    }
  }
}
</script>

<style lang="less">
.legal-auth-qr-dialog.legal-auth-qr-dialog--legal {
  .el-dialog__header {
    position: relative;
    display: flex;
    height: 42px;
    padding: 0 40px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 12px 12px 0 0;
    background: #ff7a00;
  }

  .el-dialog__title {
    color: #fff;
  }

  .el-dialog__headerbtn {
    top: 11px;
    right: 10px;
    width: 20px;
    height: 20px;
    font-size: 14px;

    .el-dialog__close,
    &:hover .el-dialog__close {
      color: #fff;
    }
  }
}

.legal-auth-qr-dialog {
  border-radius: 12px !important;

  .el-dialog__header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .el-dialog__title {
    color: #1a1a1a;
    font-size: 18px;
    font-weight: 700;
  }

  .el-dialog__body {
    padding: 30px 24px;
  }

  .qr-dialog-body {
    text-align: center;
  }

  .qr-tip {
    margin-bottom: 24px;
    color: #666;
    font-size: 14px;
  }

  .qr-code-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
  }

  .qr-company {
    margin: 0 0 20px;
    color: #1a1a1a;
    font-size: 14px;
    line-height: 20px;
    overflow-wrap: anywhere;
  }

  .qr-company-label {
    color: #666;
  }

  .qr-company-name {
    font-weight: 500;
  }

  .qr-code-box {
    display: flex;
    width: 200px;
    height: 200px;
    padding: 12px;
    align-items: center;
    justify-content: center;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
  }

  .qr-code-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .qr-code-empty,
  .qr-expire {
    color: #999;
    font-size: 13px;
  }

  .qr-countdown {
    color: var(--primary);
    font-weight: 600;
  }

  .qr-success {
    display: flex;
    margin: 0;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #2f855a;
    font-size: 14px;
    font-weight: 600;

    i {
      font-size: 18px;
    }
  }
}
</style>
